/** @format */
import isPropValid from '@emotion/is-prop-valid';
import React, { FunctionComponent, ReactNode } from 'react';
import styled from 'styled-components';

import {
    EmotionDynamicElement,
    MediaQuery,
} from '@toucan/client/global/application';

import { styles } from './constrained-wrapper.styles';

export { getMaxWidthSize, getGutterSize } from './style-helpers';

export enum ConstrainedWrapperGutter {
    /**
     * Desktop: 4.8rem
     * Mobile: 2.4rem
     */
    LARGE_GUTTER = 'large-gutter',

    /**
     * Desktop: 2.4rem
     * Mobile: 1.2rem
     */
    SMALL_GUTTER = 'small-gutter',

    /**
     * Desktop: 4.8rem
     * Mobile: 1.2rem
     */
    VARIABLE_GUTTER = 'variable-gutter',
}

export enum ConstrainedWrapperSize {
    S_1440 = 's-1440',
    S_1280 = 's-1280',
    S_1080 = 's-1080',
    S_720 = 's-720',
    /**
     * Recommended to use SMALL_GUTTER for these wrapper widths
     */
    S_552 = 's-552',
    S_312 = 's-312',
}

export enum ConstrainedWrapperType {
    PADDING = 'padding',
    GRID = 'grid',
}

export type ConstrainedWrapperProps = {
    /**
     * Child elements
     */
    children?: ReactNode;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * Allows you to change the element that's rendered
     *
     * @default 'div'
     */
    as?: React.ElementType;

    /**
     * @deprecated use default gutter size instead - new designs use new size of gutter
     *
     * The size of the gutter to use
     *
     * 1. LARGE_GUTTER
     * 2. SMALL_GUTTER
     * 3. VARIABLE_GUTTER
     *
     */
    gutter?: ConstrainedWrapperGutter;

    /**
     * The type of wrapper to use
     *
     * 1. PADDING
     * 2. GRID
     *
     * @default GRID
     */
    type?: ConstrainedWrapperType;

    /**
     * The max size of the wrapper
     *
     * 1. DESKTOP
     * 2. LARGE_TABLET
     * 3. SMALL
     * 4. TEXT
     *
     * @default DESKTOP
     * @deprecated use default size instead - new designs have a size of 1440px
     */
    size?: ConstrainedWrapperSize;

    /**
     * When the viewport reaches the media query the wrapper will
     * stretch to fill the whole space
     */
    fullWidth?: MediaQuery;
};

export type StyledConstrainedWrapperProps = ConstrainedWrapperProps;

const StyledConstrainedWrapper = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})<StyledConstrainedWrapperProps & EmotionDynamicElement>`
    ${styles}
`;

export const ConstrainedWrapper: FunctionComponent<ConstrainedWrapperProps> = ({
    children,
    as = 'div',
    type = ConstrainedWrapperType.GRID,
    gutter,
    ...otherProps
}: ConstrainedWrapperProps) => {
    return (
        <StyledConstrainedWrapper
            {...otherProps}
            type={type}
            as={as}
            gutter={gutter}
        >
            {children}
        </StyledConstrainedWrapper>
    );
};
